import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp 
} from "firebase/firestore";
import { db, auth } from "../firebase-config";

/**
 * Update watchlist for chat tracking
 */
const updateWatchList = async (userId, chatId, msgLength = 1) => {
  try {
    const updatewatchListRef = doc(db, "users", userId, "updatewatchList", chatId);
    const watchListData = {
      chatId: chatId,
      updatedAt: serverTimestamp(),
      chatLength: msgLength,
    };
    await setDoc(updatewatchListRef, watchListData);
  } catch (error) {
    console.error("Error updating watchlist:", error);
  }
};

/**
 * Send a chat message to a specific user
 * Creates a chat if one doesn't exist
 */
export const sendChatMessageToUser = async (recipientUser, messageText, senderInfo = null, metadata = null) => {
  try {
    const sender = senderInfo || {
      id: auth.currentUser.uid,
      email: auth.currentUser.email,
      displayName: auth.currentUser.displayName
    };

    // Check if chat already exists with sender
    const chatRef = collection(db, "users", sender.id, "chats");
    const { docs } = await getDocs(chatRef);
    
    let existingChatId = null;
    docs.forEach((doc) => {
      const userList = doc.data().users;
      const foundUser = userList.find(
        (chatUser) => chatUser.id === recipientUser.id && chatUser.id !== sender.id
      );
      if (foundUser) {
        existingChatId = doc.id;
      }
    });

    let chatId = existingChatId;
    let isNewChat = false;

    // Create new chat if doesn't exist
    if (!chatId) {
      isNewChat = true;
      
      // Create chat in main chats collection
      const chatStoreRef = collection(db, "chats");
      const newChatDoc = await addDoc(chatStoreRef, {
        createdAt: serverTimestamp(),
        type: "private"
      });
      chatId = newChatDoc.id;

      // Add chat reference for sender (sender first in their own list)
      const senderChatRef = doc(db, "users", sender.id, "chats", chatId);
      await setDoc(senderChatRef, {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        type: "private",
        newchat: 1,
        chatLength: 0,
        lastMessage: "",
        lastUser: "",
        users: [
          {
            id: sender.id,
            email: sender.email || "",
            displayName: sender.displayName || ""
          },
          {
            id: recipientUser.id,
            email: recipientUser.email || "",
            displayName: recipientUser.displayName || recipientUser.email || ""
          }
        ],
        chatId: chatId
      });

      // Add chat reference for recipient (recipient first in their own list)
      const recipientChatRef = doc(db, "users", recipientUser.id, "chats", chatId);
      await setDoc(recipientChatRef, {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        type: "private",
        newchat: 1,
        chatLength: 0,
        lastMessage: "",
        lastUser: "",
        users: [
          {
            id: recipientUser.id,
            email: recipientUser.email || "",
            displayName: recipientUser.displayName || recipientUser.email || ""
          },
          {
            id: sender.id,
            email: sender.email || "",
            displayName: sender.displayName || ""
          }
        ],
        chatId: chatId
      });
    }

    // Send the message with optional metadata
    const messagesRef = collection(db, "chats", chatId, "messages");
    const messageData = {
      text: messageText,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      user: sender.displayName || sender.email,
      uid: sender.id
    };
    
    // Add metadata if provided (for team invites, etc.)
    if (metadata) {
      messageData.metadata = metadata;
    }
    
    await addDoc(messagesRef, messageData);

    // Update both users' chat metadata
    const updateData = {
      updatedAt: serverTimestamp(),
      lastMessage: messageText,
      lastUser: sender.id,
      chatLength: 1 // Will be incremented if chat exists
    };

    // Update sender's chat
    const senderChatUpdateRef = doc(db, "users", sender.id, "chats", chatId);
    if (!isNewChat) {
      const senderChatDoc = await getDoc(senderChatUpdateRef);
      if (senderChatDoc.exists()) {
        const currentLength = senderChatDoc.data().chatLength || 0;
        updateData.chatLength = currentLength + 1;
      }
    }
    await updateDoc(senderChatUpdateRef, updateData);

    // Update recipient's chat
    const recipientChatUpdateRef = doc(db, "users", recipientUser.id, "chats", chatId);
    if (!isNewChat) {
      const recipientChatDoc = await getDoc(recipientChatUpdateRef);
      if (recipientChatDoc.exists()) {
        const currentLength = recipientChatDoc.data().chatLength || 0;
        updateData.chatLength = currentLength + 1;
      }
    }
    await updateDoc(recipientChatUpdateRef, updateData);

    // Update watchlists for both users
    await updateWatchList(sender.id, chatId, updateData.chatLength);
    await updateWatchList(recipientUser.id, chatId, updateData.chatLength);

    return { success: true, chatId };
  } catch (error) {
    console.error("Error sending chat message:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Send team invitation message to a user
 */
export const sendTeamInviteMessage = async (teamMember, teamName, teamAvatar, teamId, gameId, clientId) => {
  const inviteMessage = `🎮 You've been invited to join team "${teamName}" ${teamAvatar} for Jukebox Showdown! Welcome to the team! Let's win some quizzes together! 🏆`;
  
  // Include metadata for team invitation
  const metadata = {
    messageType: "teamInvite",
    teamId: teamId,
    teamName: teamName,
    teamAvatar: teamAvatar,
    gameId: gameId,
    clientId: clientId,
    invitedUserId: teamMember.id
  };
  
  return await sendChatMessageToUser(teamMember, inviteMessage, null, metadata);
};